import {
  Box,
  Card,
  CardHeader,
  Checkbox,
  CircularProgress,
  Divider,
  Heading,
  HStack,
  Image,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';
import { IPlan } from '@partiu/api/dashboard/types';
import { useAPICallAsQuery } from '@partiu/api/hooks';
import { getAgreementPlans } from '@partiu/api/subscription/api';
import { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { FaCheck } from 'react-icons/fa';
import { Button } from '../../../../../../packages/ui/components/atoms/Button';
import { ExternalLink } from '../../../../../../packages/ui/components/atoms/ExternalLink';
import { useTheme } from '../../../contexts/ThemeProvider';
import { useProfile } from '@partiu/ui/contexts/ProfileProvider';
import { useNavigate } from 'react-router-dom';

function Feature({ children }: PropsWithChildren) {
  return (
    <HStack width={'100%'}>
      <Box minWidth={'30px'}>
        <FaCheck color="green" fontSize={'18px'} />
      </Box>
      <Text>{children}</Text>
    </HStack>
  );
}

export const badges = [
  {
    name: 'Bronze',
    level: 1,
    image: 'badges/bronze.png',
  },
  {
    name: 'Prata',
    level: 2,
    image: 'badges/prata.png',
  },
  {
    name: 'Ouro',
    level: 3,
    image: 'badges/ouro.png',
  },
  {
    name: 'Safira',
    level: 4,
    image: 'badges/safira.png',
  },
  {
    name: 'Diamante',
    level: 5,
    image: 'badges/diamante.png',
  },
];

export function Plans() {
  const { id, name, colors, getValidSubscriptions, isIOS, isApp, hasIntegratedPurchaseOnMobileStore } = useTheme();
  const { profile } = useProfile();
  const [loading, setLoading] = useState(false);
  const [terms, setTerms] = useState(false);
  const { data: plans } = useAPICallAsQuery('plans', getAgreementPlans, { agreementID: id?.toString() || '0' });

  const navigate = useNavigate();

  const openVindi = useCallback((plan: IPlan) => {
    setLoading(true);

    if (isApp && isIOS && hasIntegratedPurchaseOnMobileStore) {
      navigate(`/perfil/planos/purchase-subscription-mobile/${btoa(profile?.cpf + ':' + id)}`);
    }

    setTimeout(() => {
      window.open(plan.vindiPage + '?payload=' + plan.vindiPayload, '_blank');
      setLoading(false);
    }, 2000);
  }, []);

  const disabledLevel = useMemo(() => {
    if (!profile) return;
    const subscriptions = getValidSubscriptions(profile);

    if (subscriptions.length === 0) return -1;

    return subscriptions[0].plan.id;
  }, [profile, getValidSubscriptions]);

  if (!plans || plans.length === 0) return <CircularProgress isIndeterminate color={'black'} />;

  return (
    <VStack spacing={5} align={'flex-start'} width={'100%'} maxW={'600px'}>
      <Card
        padding={{
          base: '20px',
          sm: '30px',
        }}
        width={'100%'}
      >
        <CardHeader marginBottom="20px" padding={0}>
          {disabledLevel && disabledLevel !== -1 ? (
            <Stack spacing={2} marginBottom="20px">
              <Heading fontWeight={400} textAlign={'center'} fontSize={'16px'} margin="auto">
                Parabéns! Você é um cliente {name}, Seu plano atual é{'  '}
                <span
                  style={{
                    color: colors.secondaryColor,
                    fontWeight: 700,
                  }}
                >
                  {plans.find((b) => b.id === disabledLevel)?.name}.
                </span>
              </Heading>
              <Heading fontWeight={400} textAlign={'center'} fontSize={'16px'} margin="auto">
                Você pode fazer upgrade para um plano superior a qualquer momento. e obter mais vantagens.
              </Heading>
            </Stack>
          ) : (
            <Stack spacing={2} marginBottom="15px">
              <Heading fontWeight={400} textAlign={'center'} fontSize={'16px'} margin="auto">
                Identificamos que você ainda não possui um plano ativo.
              </Heading>
              <Heading fontWeight={400} textAlign={'center'} fontSize={'16px'} margin="auto">
                Ao adquirir um plano de internet com a {name}, você ganha acesso ao nosso Programa de Relacionamento.
              </Heading>
              <Heading fontWeight={400} textAlign={'center'} fontSize={'16px'} margin="auto">
                Escolha um plano abaixo que mais combina com você e aproveite!
              </Heading>
            </Stack>
          )}
        </CardHeader>

        <Tabs isFitted width={'100%'} variant="soft-rounded">
          <TabList width={'100%'} overflowX={'auto'} border={'1px solid #dedede'} borderRadius={'20px'} gap={0}>
            {plans
              .sort((a, b) => a.level - b.level)
              .map((p) => (
                <Tab
                  textOverflow={'ellipsis'}
                  whiteSpace={'nowrap'}
                  padding={'10px 15px'}
                  _selected={{ color: 'white', backgroundColor: colors.secondaryColor }}
                  key={p.id}
                >
                  {p.name}
                </Tab>
              ))}
          </TabList>
          <TabPanels>
            {plans
              .sort((a, b) => a.level - b.level)
              .map((p) => (
                <TabPanel key={p.id}>
                  <VStack spacing={6} w={'100%'} align={'flex-start'}>
                    <Box margin="auto">
                      {badges.find((b) => b.level === p.level) && (
                        <Image
                          src={`/${badges.find((b) => b.level === p.level)?.image}`}
                          alt={p.name}
                          height={'150px'}
                        />
                      )}
                    </Box>
                    <HStack
                      alignSelf={'center'}
                      padding={'20px 0px'}
                      spacing={2}
                      justify={'flex-start'}
                      align="flex-start"
                    >
                      <Heading margin={'0px'} fontSize={'18px'}>
                        R$
                      </Heading>
                      <Heading lineHeight={'50px'} fontSize={'60px'}>
                        {p.value.split('.')[0]}
                      </Heading>
                      <Heading>,{p.value.split('.')[1]}</Heading>
                      <Heading alignSelf={'flex-end'} fontSize={'12px'}>
                        Mensais
                      </Heading>
                    </HStack>
                    <Divider />
                    <VStack>
                      <Feature>Garanta {p.accessAmount} acessos à plataforma</Feature>
                      <Feature>Mais de 1000 descontos em lojas próximas de você.</Feature>
                      <Feature>Dezenas de parceiros ecommerce com descontos</Feature>
                    </VStack>

                    {disabledLevel !== p.id && (
                      <VStack
                        margin="auto"
                        spacing={6}
                        width={{
                          base: '100%',
                          sm: 'auto',
                        }}
                      >
                        <Checkbox isChecked={terms} onChange={(e) => setTerms(e.target.checked)}>
                          Estou de acordo com os{' '}
                          <ExternalLink color={colors.primaryColor} to={'/legal/termos-de-uso'} target="blank">
                            Termos de Uso
                          </ExternalLink>{' '}
                          e{' '}
                          <ExternalLink
                            color={colors.primaryColor}
                            to={'/legal/politica-de-privacidade'}
                            target="blank"
                          >
                            Política de Privacidade
                          </ExternalLink>
                        </Checkbox>

                        <Button
                          isLoading={loading}
                          onClick={() => openVindi(p)}
                          isDisabled={!terms || disabledLevel === p.id}
                        >
                          {disabledLevel === p.id ? 'Esse é seu plano atual' : 'Assinar'}
                        </Button>
                      </VStack>
                    )}
                  </VStack>
                </TabPanel>
              ))}
          </TabPanels>
        </Tabs>
      </Card>
    </VStack>
  );
}
